import React, { ChangeEvent } from "react";
import { Grid, TextField } from "@mui/material";

import {
  convertNumberToWords,
  formatCurrency,
  parseAmount,
  roundCurrency,
} from "./common";
import {
  InternalCalculations,
  InternalInvoiceBase,
  InternalInvoiceBaseFields,
  createInternalBaseForm,
  generateInternalInvoicePdf,
  validateInternalBaseFields,
} from "./internalCommon";

export interface InternalSgstCgstForm extends InternalInvoiceBase {
  sgstRate: string;
  cgstRate: string;
}

export const createInternalSgstCgstForm = (): InternalSgstCgstForm => ({
  ...createInternalBaseForm(),
  sgstRate: "9",
  cgstRate: "9",
});

export const calculateInternalSgstCgst = (
  form: InternalSgstCgstForm
): InternalCalculations => {
  const taxableValue = parseAmount(form.taxableValue);
  const sgstRate = Number.parseFloat(form.sgstRate) || 0;
  const cgstRate = Number.parseFloat(form.cgstRate) || 0;

  if (!Number.isFinite(taxableValue)) {
    return {
      taxableValue: Number.NaN,
      taxOneLabel: "",
      taxTwoLabel: undefined,
      taxOneRate: sgstRate,
      taxTwoRate: cgstRate,
      taxOneAmount: Number.NaN,
      taxTwoAmount: Number.NaN,
      totalAmount: Number.NaN,
      amountInWords: "",
    };
  }

  const sgstAmount = roundCurrency((taxableValue * sgstRate) / 100);
  const cgstAmount = roundCurrency((taxableValue * cgstRate) / 100);
  const total = roundCurrency(taxableValue + sgstAmount + cgstAmount);

  return {
    taxableValue,
    taxOneLabel: `SGST @${sgstRate}%`,
    taxTwoLabel: `CGST @${cgstRate}%`,
    taxOneRate: sgstRate,
    taxTwoRate: cgstRate,
    taxOneAmount: sgstAmount,
    taxTwoAmount: cgstAmount,
    totalAmount: total,
    amountInWords: convertNumberToWords(total),
  };
};

export const validateInternalSgstCgstForm = (form: InternalSgstCgstForm) => {
  const validation = validateInternalBaseFields(form);
  const taxable = parseAmount(form.taxableValue);
  if (!Number.isFinite(taxable) || taxable <= 0) {
    validation.taxableValue = "Enter a valid amount";
  }

  const sgst = Number.parseFloat(form.sgstRate);
  if (!Number.isFinite(sgst) || sgst < 0) {
    validation.sgstRate = "Enter a valid rate";
  }

  const cgst = Number.parseFloat(form.cgstRate);
  if (!Number.isFinite(cgst) || cgst < 0) {
    validation.cgstRate = "Enter a valid rate";
  }

  return validation;
};

export const generateInternalSgstCgstInvoicePdf = (
  form: InternalSgstCgstForm,
  calculations: InternalCalculations
) => {
  const head = [
    [
      "Description",
      "HSN/SAC",
      "Taxable Value",
      calculations.taxOneLabel,
      calculations.taxTwoLabel || "",
      "Total",
    ],
  ];

  const body = [
    [
      form.description,
      form.hsnSac,
      formatCurrency(calculations.taxableValue),
      formatCurrency(calculations.taxOneAmount),
      formatCurrency(calculations.taxTwoAmount ?? 0),
      formatCurrency(calculations.totalAmount),
    ],
    [
      "Total",
      "",
      formatCurrency(calculations.taxableValue),
      formatCurrency(calculations.taxOneAmount),
      formatCurrency(calculations.taxTwoAmount ?? 0),
      formatCurrency(calculations.totalAmount),
    ],
  ];

  generateInternalInvoicePdf(form, calculations, head, body, "SGST_CGST");
};

interface InternalSgstCgstFieldsProps {
  form: InternalSgstCgstForm;
  errors: Record<string, string>;
  calculations: InternalCalculations;
  onChange: (
    field: keyof InternalSgstCgstForm
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRegenerateUuid: () => void;
  onApplyDefaultInvoiceNumber: () => void;
}

export const InternalSgstCgstFields: React.FC<InternalSgstCgstFieldsProps> = ({
  form,
  errors,
  calculations,
  onChange,
  onRegenerateUuid,
  onApplyDefaultInvoiceNumber,
}) => {
  const onBaseChange = (field: keyof InternalInvoiceBase) =>
    onChange(field as keyof InternalSgstCgstForm);

  return (
    <InternalInvoiceBaseFields
      form={form}
      errors={errors}
      onBaseChange={onBaseChange}
      onRegenerateUuid={onRegenerateUuid}
      onApplyInvoiceNumber={onApplyDefaultInvoiceNumber}
      calculations={calculations}
    >
      <Grid item xs={12} md={6}>
        <TextField
          label="SGST Percentage"
          value={form.sgstRate}
          onChange={onChange("sgstRate")}
          fullWidth
          error={Boolean(errors.sgstRate)}
          helperText={errors.sgstRate}
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="CGST Percentage"
          value={form.cgstRate}
          onChange={onChange("cgstRate")}
          fullWidth
          error={Boolean(errors.cgstRate)}
          helperText={errors.cgstRate}
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
        />
      </Grid>
    </InternalInvoiceBaseFields>
  );
};
